import { CLIENT_FACTSHEET_ENUM_OPTIONS } from '../constants/client-factsheet';

export const MEETING_NOTE_FACTSHEET_SYNC_PROMPT = 'meeting_note_factsheet_sync';

export const MEETING_NOTE_FACTSHEET_FIELDS = {
  occupation: {
    label: '職業',
    labelId: 'Occupation',
    inputType: 'text',
    valueType: 'string'
  },
  workLocation: {
    label: '工作地點',
    labelId: 'Work Location',
    inputType: 'text',
    valueType: 'string'
  },
  homeLocation: {
    label: '居住地點',
    labelId: 'Home Location',
    inputType: 'text',
    valueType: 'string'
  },
  nationality: {
    label: '國籍',
    labelId: 'Nationality',
    inputType: 'text',
    valueType: 'string'
  },
  birthPlace: {
    label: '出生地',
    labelId: 'Place of Birth',
    inputType: 'text',
    valueType: 'string'
  },
  smoker: {
    label: '吸煙習慣',
    labelId: 'Smoker',
    inputType: 'enum',
    valueType: 'string',
    options: CLIENT_FACTSHEET_ENUM_OPTIONS.smoker
  },
  marriageStatus: {
    label: '婚姻狀況',
    labelId: 'Marriage Status',
    inputType: 'enum',
    valueType: 'string',
    options: CLIENT_FACTSHEET_ENUM_OPTIONS.marriageStatus
  },
  child: {
    label: '子女數目',
    labelId: 'No. of Child',
    inputType: 'number',
    valueType: 'number'
  },
  monthlyIncome: {
    label: '每月收入',
    labelId: 'Monthly Income',
    inputType: 'number',
    valueType: 'number'
  },
  monthlyExpense: {
    label: '每月支出',
    labelId: 'Monthly Expense',
    inputType: 'number',
    valueType: 'number'
  },
  liquidAsset: {
    label: '流動資產',
    labelId: 'Liquid Asset',
    inputType: 'number',
    valueType: 'number'
  },
  debt: { label: '負債', labelId: 'Debt', inputType: 'number', valueType: 'number' },
  MPF: { label: '強積金', labelId: 'MPF', inputType: 'text', valueType: 'string' },
  insurance: {
    label: '保險',
    labelId: 'Insurance',
    inputType: 'text',
    valueType: 'string'
  },
  investment: {
    label: '投資',
    labelId: 'Investment',
    inputType: 'text',
    valueType: 'string'
  },
  budget: { label: '預算', labelId: 'Budget', inputType: 'text', valueType: 'string' },
  interest: { label: '興趣', labelId: 'Interest', inputType: 'text', valueType: 'string' },
  latestMeetingDate: {
    label: '最近會面',
    labelId: 'Latest Meeting',
    inputType: 'date',
    valueType: 'date'
  },
  nextMeetingDate: {
    label: '下次會面',
    labelId: 'next-meeting-date',
    inputType: 'date',
    valueType: 'date'
  }
};

/**
 * Keep the complete remark as the sync source so the analyzer can extract both
 * client factsheet values and meeting dates from the same note history.
 */
export const getMeetingNoteForSync = remark => ({
  note: typeof remark === 'string' ? remark : ''
});

export const buildMeetingNoteFingerprint = ({ clientId, remark }) => [clientId || '', remark || ''].join('::');

const isEmptyValue = value => value === undefined || value === null || (typeof value === 'string' && !value.trim());
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const isValidDateOnly = value => {
  if (typeof value !== 'string' || !DATE_ONLY_PATTERN.test(value)) return false;
  const date = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(date.valueOf()) && date.toISOString().slice(0, 10) === value;
};

const normalizeDateOnly = value => {
  if (isEmptyValue(value)) return value;
  if (typeof value === 'string') {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match && isValidDateOnly(match[1])) return match[1];
  }
  if (value instanceof Date && !Number.isNaN(value.valueOf())) return value.toISOString().slice(0, 10);
  return value;
};

const normalizeValue = (field, value) =>
  MEETING_NOTE_FACTSHEET_FIELDS[field]?.valueType === 'date' ? normalizeDateOnly(value) : value;

export const valuesEqual = (left, right, field) => {
  left = normalizeValue(field, left);
  right = normalizeValue(field, right);
  if (isEmptyValue(left) || isEmptyValue(right)) return isEmptyValue(left) && isEmptyValue(right);
  if (
    (typeof left === 'number' || typeof left === 'string') &&
    (typeof right === 'number' || typeof right === 'string')
  ) {
    return String(left) === String(right);
  }
  return left === right;
};

const parseJsonResult = result => {
  if (Array.isArray(result)) return result;
  if (typeof result !== 'string') return null;

  const text = result
    .trim()
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/\s*```$/, '');
  try {
    return JSON.parse(text);
  } catch (_) {
    return null;
  }
};

const isValidValue = (field, value) => {
  const metadata = MEETING_NOTE_FACTSHEET_FIELDS[field];
  if (!metadata || isEmptyValue(value)) return false;
  if (metadata.valueType === 'number') return typeof value === 'number' && Number.isFinite(value);
  if (metadata.valueType === 'date') return isValidDateOnly(value);
  if (metadata.options && !metadata.options.includes(value)) return false;
  return typeof value === 'string' && !!value.trim();
};

/** Strictly validate and normalize the fixed AI response contract. */
export const normalizeMeetingFactsheetAiResult = result => {
  const parsed = parseJsonResult(result);
  if (!Array.isArray(parsed)) return null;

  const seen = new Set();
  const normalized = [];
  for (const item of parsed) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
    const keys = Object.keys(item).sort();
    if (keys.join(',') !== 'confidence,evidence,field,value') return null;
    if (!MEETING_NOTE_FACTSHEET_FIELDS[item.field] || seen.has(item.field)) return null;
    if (!['explicit', 'inferred', 'uncertain'].includes(item.confidence)) return null;
    if (typeof item.evidence !== 'string' || !item.evidence.trim()) return null;
    if (!isValidValue(item.field, item.value)) return null;

    seen.add(item.field);
    normalized.push({
      field: item.field,
      value: item.value,
      confidence: item.confidence,
      evidence: item.evidence.trim()
    });
  }
  return normalized;
};

export const buildMeetingFactsheetDiff = (aiResult, factsheet = {}) => {
  const normalized = normalizeMeetingFactsheetAiResult(aiResult);
  if (!normalized) return null;

  return normalized
    .filter(({ field, value }) => !isEmptyValue(value) && !valuesEqual(factsheet[field], value, field))
    .map(item => ({
      ...item,
      currentValue: normalizeValue(item.field, factsheet[item.field]),
      metadata: MEETING_NOTE_FACTSHEET_FIELDS[item.field]
    }));
};

export const buildMeetingFactsheetSyncPayload = (suggestions, selected, values) => {
  const set = {};
  const expected = {};

  for (const item of suggestions || []) {
    if (!selected?.[item.field]) continue;

    const value = values?.[item.field];
    if (isEmptyValue(value)) return null;

    if (item.metadata.valueType === 'number') {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) return null;
      set[item.field] = numericValue;
    } else if (!isValidValue(item.field, value)) {
      return null;
    } else {
      set[item.field] = normalizeValue(item.field, value);
    }

    expected[item.field] = item.currentValue === undefined ? null : normalizeValue(item.field, item.currentValue);
  }

  return Object.keys(set).length ? { set, expected } : { set: {}, expected: {} };
};

export const isMeetingFactsheetSyncConflict = error => error?.response?.status === 409 && !!error?.payload?.conflict;

/**
 * Serialize the current values for the fields that the meeting-note sync can
 * update. Keep this client-side payload scoped to the same allow-list used to
 * validate the AI response, so unrelated factsheet data is not added to the
 * prompt.
 */
export const getMeetingNoteFactsheetInfo = (factsheet = {}) =>
  JSON.stringify(
    Object.keys(MEETING_NOTE_FACTSHEET_FIELDS).reduce((info, field) => {
      info[field] = normalizeValue(field, factsheet?.[field] ?? null);
      return info;
    }, {})
  );

export const getMeetingNotePromptData = (note, factsheet) => ({
  remark: note,
  factsheetInfo: getMeetingNoteFactsheetInfo(factsheet)
});

const MEETING_FACTSHEET_SYNC_SEEN_KEY = 'meeting-factsheet-sync-seen';

const getStoredFingerprints = () => {
  try {
    const stored = JSON.parse(sessionStorage.getItem(MEETING_FACTSHEET_SYNC_SEEN_KEY) || '[]');
    return Array.isArray(stored) ? stored : [];
  } catch (_) {
    return [];
  }
};

const rememberFingerprint = fingerprint => {
  try {
    const fingerprints = new Set(getStoredFingerprints());
    fingerprints.add(fingerprint);
    sessionStorage.setItem(MEETING_FACTSHEET_SYNC_SEEN_KEY, JSON.stringify([...fingerprints]));
  } catch (_) {
    // Session storage is optional in native/private browsing contexts.
  }
};

/**
 * Own the post-save analysis lifecycle without coupling it to MeetingDialog.
 * The UI only decides how to present the returned result or error.
 */
export const createMeetingNoteFactsheetSyncRunner = ({
  analyze,
  onStart: defaultOnStart,
  getSeenFingerprints = getStoredFingerprints,
  rememberSeenFingerprint = rememberFingerprint
} = {}) => {
  const inFlight = new Set();

  const run = async ({ clientId, factsheet, remark, enabled = true, forceRetry = false, onStart } = {}) => {
    if (!enabled) return { status: 'skipped', reason: 'disabled' };

    const meetingNote = getMeetingNoteForSync(remark);
    if (!meetingNote.note.trim()) return { status: 'skipped', reason: 'empty' };

    const fingerprint = buildMeetingNoteFingerprint({
      clientId,
      remark: meetingNote.note
    });
    if (!forceRetry && (inFlight.has(fingerprint) || getSeenFingerprints().includes(fingerprint))) {
      return { status: 'skipped', reason: 'duplicate', fingerprint };
    }

    if (typeof analyze !== 'function') {
      return { status: 'error', fingerprint, error: new Error('Meeting factsheet sync analyzer is not configured') };
    }

    inFlight.add(fingerprint);
    try {
      try {
        const callbackResult = (onStart || defaultOnStart)?.({ fingerprint });
        if (callbackResult && typeof callbackResult.catch === 'function') callbackResult.catch(() => {});
      } catch (_) {
        // Lifecycle callbacks are presentation-only and must never affect sync.
      }

      const responseText = await analyze(
        MEETING_NOTE_FACTSHEET_SYNC_PROMPT,
        getMeetingNotePromptData(meetingNote.note, factsheet)
      );
      const normalizedResult = normalizeMeetingFactsheetAiResult(responseText);
      const suggestions = buildMeetingFactsheetDiff(normalizedResult, factsheet);
      if (!suggestions) throw new Error('Invalid meeting factsheet sync response');

      rememberSeenFingerprint(fingerprint);
      return { status: 'success', fingerprint, suggestions };
    } catch (error) {
      return { status: 'error', fingerprint, error };
    } finally {
      inFlight.delete(fingerprint);
    }
  };

  return { run };
};
